// HeroSection.jsx
import React from 'react';
import HoverGrid from './HoverGrid';
import { Reveal } from './Reveal';
import ModernButton from './ModernButton';
import ModernContactForm from './ModernContactForm';
import ValueProps from './ValueProps';
import { motion } from 'framer-motion';

function HeroSection({ title, description, buttonText, buttonLink  }) {
  return (
    <div className="relative min-h-screen h-full bg-neutral-950 flex justify-center content-center flex-wrap overflow-hidden">
      {/* Canvas Background */}
      <HoverGrid />

      {/* Gradient Overlay for better readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/40 via-transparent to-neutral-950/60 pointer-events-none" />

      {/* Content Overlay */}
      <div className="relative pointer-events-none h-full inset-0 flex flex-col items-start justify-center pt-32 md:pt-24 pb-8 px-8 mx-auto w-full max-w-7xl">
        <div className="grid max-w-screen-xl py-8 mx-auto lg:gap-8 xl:gap-10 lg:py-16 lg:grid-cols-12">
          <div className="mr-auto place-self-center lg:col-span-7 space-y-6">
            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 flex-wrap"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-500/20 to-violet-500/20 border border-indigo-500/30 backdrop-blur-sm">
                <svg className="w-4 h-4 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold text-indigo-200">11+ Years of Experience</span>
              </span>

            </motion.div>

            {/* Main Title with Gradient */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1 className="max-w-2xl mb-4 text-4xl font-extrabold leading-tight md:text-5xl xl:text-6xl">
                <span className="bg-gradient-to-r from-white via-indigo-100 to-violet-100 bg-clip-text text-transparent">
                  {title}
                </span>
              </h1>
            </motion.div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="max-w-2xl mb-6 font-light text-gray-300 lg:mb-8 md:text-lg lg:text-xl leading-relaxed"
            >
              {description}
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {buttonText && buttonLink && (
                <ModernButton text={buttonText} link={buttonLink} />
              )}
            </motion.div>

            {/* Value Props */}
            <ValueProps />
          </div>

          <div className="mt-8 lg:mt-0 lg:col-span-5 lg:flex">
            <Reveal>
              <ModernContactForm />
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroSection;